import { COUNTRIES } from './countries';
import type { BBox } from './geo';

export interface RegionDefinition {
  id: string;
  name: string;
  tagline: string;
  emoji: string;
  countries: readonly string[];
  /** Characteristic size of the region; drives how fast points decay with distance. */
  scaleKm: number;
  /** Initial view of the guess map. `null` means the whole world. */
  view: BBox | null;
}

const ALL_COUNTRIES = COUNTRIES.map((country) => country.code);

const codes = (list: string) => list.split(/\s+/).filter(Boolean);

export const REGIONS: readonly RegionDefinition[] = [
  {
    id: 'world',
    name: 'World',
    tagline: 'Anywhere with a road and a camera.',
    emoji: '🌍',
    countries: ALL_COUNTRIES,
    scaleKm: 20000,
    view: null,
  },
  {
    id: 'europe',
    name: 'Europe',
    tagline: 'Roundabouts, bollards and church spires.',
    emoji: '🏰',
    countries: codes(
      'AD AL AT BA BE BG CH CY CZ DE DK EE ES FI FO FR GB GR HR HU IE IM IS IT JE LI LT LU LV ME MK MT NL NO PL PT RO RS RU SE SI SK SM UA',
    ),
    scaleKm: 4500,
    view: [-25, 34, 45, 71],
  },
  {
    id: 'north-america',
    name: 'North America',
    tagline: 'From the Yukon to the Caribbean.',
    emoji: '🦬',
    countries: codes('US CA MX GT CR PA DO PR VI CW BM'),
    scaleKm: 7000,
    view: [-130, 7, -55, 62],
  },
  {
    id: 'south-america',
    name: 'South America',
    tagline: 'Andes, pampas and endless red dirt roads.',
    emoji: '🦙',
    countries: codes('BR AR CL CO EC PE BO UY PY FK'),
    scaleKm: 6000,
    view: [-82, -56, -34, 13],
  },
  {
    id: 'asia',
    name: 'Asia',
    tagline: 'Megacities, rice terraces and steppe.',
    emoji: '🏯',
    countries: codes('JP KR TW HK MO TH MY SG ID PH KH LA VN IN BD LK NP BT MN KZ KG TR IL PS JO LB AE QA OM'),
    scaleKm: 8000,
    view: [25, -11, 150, 55],
  },
  {
    id: 'africa',
    name: 'Africa',
    tagline: 'Savannah, coastlines and bright market streets.',
    emoji: '🦒',
    countries: codes('ZA LS SZ BW NA KE UG RW TZ NG GH SN TN MG RE'),
    scaleKm: 6000,
    view: [-20, -36, 52, 38],
  },
  {
    id: 'oceania',
    name: 'Oceania',
    tagline: 'Outback roads and island hopping.',
    emoji: '🦘',
    countries: codes('AU NZ GU MP AS'),
    scaleKm: 5500,
    view: [110, -48, 180, -8],
  },
  {
    id: 'southeast-asia',
    name: 'Southeast Asia',
    tagline: 'Scooters, palms and tropical rain.',
    emoji: '🛵',
    countries: codes('TH MY SG ID PH KH LA VN'),
    scaleKm: 4500,
    view: [92, -11, 128, 24],
  },
  {
    id: 'nordics',
    name: 'Nordics',
    tagline: 'Fjords, birches and red wooden houses.',
    emoji: '🫎',
    countries: codes('NO SE FI DK IS FO'),
    scaleKm: 2500,
    view: [-25, 54, 32, 71.5],
  },
  {
    id: 'british-isles',
    name: 'British Isles',
    tagline: 'Hedgerows, sheep and drizzle.',
    emoji: '🫖',
    countries: codes('GB IE IM JE'),
    scaleKm: 1100,
    view: [-11, 49.8, 2, 59],
  },
  {
    id: 'france',
    name: 'France',
    tagline: 'Boulangeries, plane trees and ronds-points.',
    emoji: '🥖',
    countries: ['FR'],
    scaleKm: 1100,
    view: [-5.5, 41.2, 9.8, 51.2],
  },
  {
    id: 'usa',
    name: 'United States',
    tagline: 'Fifty shades of highway.',
    emoji: '🦅',
    countries: ['US'],
    scaleKm: 4500,
    view: [-125, 24.4, -66.9, 49.5],
  },
  {
    id: 'brazil',
    name: 'Brazil',
    tagline: 'Big, green and wonderfully confusing.',
    emoji: '🦜',
    countries: ['BR'],
    scaleKm: 4000,
    view: [-74, -34, -34, 5.3],
  },
  {
    id: 'japan',
    name: 'Japan',
    tagline: 'Vending machines and mountain passes.',
    emoji: '🗻',
    countries: ['JP'],
    scaleKm: 2000,
    view: [128, 30, 146, 46],
  },
];

export const DEFAULT_REGION_ID = 'world';

const REGIONS_BY_ID = new Map(REGIONS.map((region) => [region.id, region]));

export function getRegion(id: string): RegionDefinition | undefined {
  return REGIONS_BY_ID.get(id);
}

export const REGION_IDS = REGIONS.map((region) => region.id) as [string, ...string[]];
